
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import _truncate from 'lodash/truncate';
import { TeamUserMixin } from '@/mixins';
import AddEditWorkspace from './AddEditWorkspace.vue';
import ErrorState from '@/components/states/ErrorState.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import WorkspaceCard from '@/views/Workspaces/WorkspaceCard.vue';
import InfoMessageSmall from '@/components/base/InfoMessageSmall.vue';
import { Team, Workspace } from '@run-diffusion/shared';
import EmptyState from '@/components/states/EmptyState.vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';

export default Vue.extend({
	name: 'Workspaces',
	mixins: [
		TeamUserMixin,
	],
	props: {
		currTeam: { type: Object, default: null },
		currTeamUserRole: { type: Object, default: null },
	},
	data () {
		return {
			MAX_WORKSPACES: 16,
			loading: false,
			hasError: false,

			addEditWorkspaceConfig: {
				open: false,
				editMode: false,
				workspace: null,
			},

			localWorkspaces: [] as Workspace[],
		};
	},
	computed: {
		...mapGetters([
			'displayLabels',
			'activeDomainClaimRestriction',
		]),
		// Permission + active checks must run against the team this page is showing (route
		// teamId), not the signed-in team in the store. A company admin viewing another
		// team's dashboard would otherwise see their own team's RD_ADMIN answer here.
		hasCurrTeamRdAdminPermission (): boolean {
			if (!this.currTeam) return false;
			return this.checkRdAdminPermission(this.currTeam, this.currTeamUserRole);
		},
		isCurrTeamActive (): boolean {
			return !!this.currTeam?.isActive;
		},
		maxWorkspacesReached () {
			return this.localWorkspaces.length >= this.MAX_WORKSPACES;
		},
		addWorkspaceBlockedByDomainClaim (): boolean {
			return !!this.activeDomainClaimRestriction;
		},
		domainClaimAddWorkspaceTooltip (): string {
			if (!this.activeDomainClaimRestriction) return '';
			const base = getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team);
			return `${base} You cannot add a new Space for this ${this.displayLabels.team} until you switch to an allowed account in the toolbar or update your sign-in email.`;
		},
	},
	watch: {
		currTeam: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				const newTeamId: string = newVal?.id || null;
				const oldTeamId: string = oldVal?.id || null;
				if (newTeamId !== oldTeamId) {
					this.bindLocalWorkspaces(newVal);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		_truncate,
		async bindLocalWorkspaces (team: Team) {
			this.hasError = false;
			if (!team) {
				this.localWorkspaces = [];
				return;
			}

			this.loading = true;
			try {
				// index created: workspaces: deletedAt Ascending teamId Ascending createdAt Ascending __name__ Ascending
				const workspacesRef = db.collection('workspaces')
					.where('teamId', '==', team.id)
					.where('deletedAt', '==', null)
					.orderBy('createdAt', 'asc')
					.limit(100);

				await this.$bind(
					'localWorkspaces',
					workspacesRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error loading workspaces:', error);
				this.hasError = true;
			} finally {
				this.loading = false;
			}
		},
		openAddWorkspaceDrawer () {
			if (this.addWorkspaceBlockedByDomainClaim) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team),
					show: true,
				});
				return;
			}
			this.addEditWorkspaceConfig = {
				open: true,
				editMode: false,
				workspace: null,
			};
		},
		onAddEditInput (open: boolean) {
			this.addEditWorkspaceConfig = {
				...this.addEditWorkspaceConfig,
				open: !!open,
				workspace: null,
			};
		},
		onEditWorkspace (workspace: Workspace) {
			this.addEditWorkspaceConfig = {
				open: true,
				editMode: true,
				workspace,
			};
		},
	},
	components: {
		EmptyState,
		InfoMessageSmall,
		WorkspaceCard,
		AddEditWorkspace,
		ErrorState,
		LoadingState,
	},
});
